/**
 * Single home for turning a `MarginHistory.reason` code into a Polish sentence.
 *
 * The backend writes the reason as a bare uppercase code (see
 * `backend/src/services/orderService.js` `recalculateMargin` callers). The order
 * card must never show that code to an operator, so every code known to the
 * backend has an entry here, and anything unknown still renders as a readable
 * Polish phrase with the code in parentheses instead of a bare enum.
 */

/** Every reason code the backend currently writes to `MarginHistory.reason`. */
export const MARGIN_HISTORY_REASON_CODES = [
  'COST_ADDED',
  'COST_MODIFIED',
  'COST_DELETED',
  // WP5: another order, a delivery or an average-cost edit changed this
  // order's stock allocation (orderCostPlanService).
  'COST_PLAN_REALLOCATED',
  'DELIVERY_REVERTED',
  'PRODUCTS_UPDATED',
  'PURCHASE_INVOICE_RECEIVED',
  'PZ_CORRECTION',
  'WZ_CORRECTION',
  'STOCK_VALUATION',
  'TRANSPORT_ADDED',
  'TRANSPORT_UPDATED',
  'TRANSPORT_DELETED',
  'WZ_RELEASED',
] as const;

export type MarginHistoryReasonCode = (typeof MARGIN_HISTORY_REASON_CODES)[number];

const REASON_KEY_PREFIX = 'orders:orderDetail.marginHistoryReasons.';

type TranslateFn = (key: string, params?: Record<string, unknown>) => string;

/**
 * Polish label for one margin-history entry. An unknown or missing code falls
 * back to `Inna zmiana (KOD)` rather than leaking the raw code on its own.
 */
export function getMarginHistoryReasonLabel(
  reason: string | null | undefined,
  t: TranslateFn
): string {
  const code = typeof reason === 'string' ? reason.trim() : '';
  if (!code) return t('orders:orderDetail.marginHistoryReasonUnknownEmpty');

  const isKnown = (MARGIN_HISTORY_REASON_CODES as readonly string[]).includes(code);
  if (!isKnown) return t('orders:orderDetail.marginHistoryReasonUnknown', { code });

  return t(`${REASON_KEY_PREFIX}${code}`, {
    defaultValue: t('orders:orderDetail.marginHistoryReasonUnknown', { code }),
  });
}
